import styled from "styled-components";
import { useDoubleClick } from "common/hooks";

const StyledContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const FieldWrapper = styled.div`
  display: inline-flex;
  max-width: ${(props) => props.maxWidthField + "px"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0 0 10px;
  gap: 5px;
  align-items: center;
  cursor: pointer;

  .square {
    width: 12px;
    min-width: 12px;
    height: 12px;
    border: 2px solid;
    border-radius: 6px;
  }

  label {
    user-select: none;
    margin-bottom: 0;
    font-size: 13px;
    font-weight: normal;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

function LegendItem({
  color,
  name,
  label,
  tooltip,
  isSelected,
  onSelect,
  onContextMenu,
  maxWidthField = 130,
  hasNameChange = false,
}) {
  const field = hasNameChange
    ? { label, name, color }
    : { label, name: label, color };
  const onClick = () => onSelect(field, "single");
  const onDoubleClick = () => onSelect(field, "double");
  const handleClick = useDoubleClick(onClick, onDoubleClick);
  const handleContextClick = (e, name) => {
    if (onContextMenu) {
      e.preventDefault();
      onContextMenu(name);
    }
  };

  return (
    <FieldWrapper
      onClick={handleClick}
      onContextMenu={(e) => handleContextClick(e, label)}
      maxWidthField={maxWidthField}
      title={tooltip}
    >
      <div
        className="square"
        style={{
          backgroundColor: isSelected ? color : "transparent",
          borderColor: color,
        }}
      ></div>
      <label title={tooltip || label}> {label}</label>
    </FieldWrapper>
  );
}

function DPIAnalyticsLiveLegend({
  fields,
  onChange,
  value,
  rest,
  onContextMenu,
  maxWidthField,
  hasNameChange = false,
}) {
  function handleSelect(field, selection) {
    // On double click, select only the clicked item.
    if (selection === "double") {
      onChange(new Set([field.name]));
      return;
    }

    if (value.size === 1 && value.has(field.name)) {
      const allFieldsSet = new Set(fields.map((f) => f.name));
      onChange(allFieldsSet);
      return;
    }

    const newFilterSet = new Set(value);
    if (newFilterSet.has(field.name)) {
      newFilterSet.delete(field.name);
    } else {
      newFilterSet.add(field.name);
    }
    onChange(newFilterSet);
  }

  const fieldRest =
    rest !== false
      ? { label: "rest", name: "rest", color: rest["color"] }
      : null;

  return (
    <StyledContainer className="cancel-this-z-index">
      {fields.map((field) => {
        const { name, label, color, tooltip } = field;
        if (field.name === "rest") {
          return null;
        }
        return (
          <LegendItem
            key={`legend-${label}`}
            label={label}
            color={color}
            tooltip={tooltip}
            name={name}
            isSelected={value.has(name)}
            onSelect={handleSelect}
            onContextMenu={onContextMenu}
            maxWidthField={maxWidthField}
            hasNameChange={hasNameChange}
          />
        );
      })}
      {fieldRest ? (
        <LegendItem
          key="legend-rest"
          label="rest"
          color={fieldRest.color}
          isSelected={value.has("rest")}
          onSelect={handleSelect}
          onContextMenu={onContextMenu}
          maxWidthField={maxWidthField}
          hasNameChange={hasNameChange}
        />
      ) : null}
    </StyledContainer>
  );
}

export default DPIAnalyticsLiveLegend;
